import React, { useMemo } from "react";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Container from "react-bootstrap/Container";
import { colors, resolveColor, font } from "utils/styles";
import ButtonGroup from "./ButtonGroup";
import renderItem from "utils/renderItem";

export default function ActionItems(props) {
	const fields = props.data?.fields;
	
	const spacingStyles = useMemo(() => ({
		marginTop: fields?.marginTop || 4,
		paddingTop: fields?.paddingTop || 4,
		paddingBottom: fields?.paddingBottom || 4,
		marginBottom: fields?.marginBottom || 4,
		marginTopMobile: (fields?.marginTop || 4) / 2,
		paddingTopMobile: (fields?.paddingTop || 4) / 2,
		paddingBottomMobile: (fields?.paddingBottom || 4) / 2,
		marginBottomMobile: (fields?.marginBottom || 4) / 2
	}), [fields?.marginTop, fields?.paddingTop, fields?.paddingBottom, fields?.marginBottom]);
	
	// Extract font sizes from Contentful fields
	const fontSizes = useMemo(() => {
		const headerTitleComponent = fields?.components?.find(
			comp => comp.sys?.contentType?.sys?.id === "title"
		);
		
		const firstMediaCard = fields?.components?.find(
			comp => comp.sys?.contentType?.sys?.id === "mediaCardSection"
		)?.fields?.mediaCards?.[0];
		
		const firstTitleComponent = firstMediaCard?.contents?.find(
			content => content.sys?.contentType?.sys?.id === "title"
		);
		
		const firstButtonComponent = firstMediaCard?.contents?.find(
			content => content.sys?.contentType?.sys?.id === "buttonGroup"
		);
		
		return {
			headerFontSize: headerTitleComponent?.fields?.fontSize || 40,
			titleFontSize: firstTitleComponent?.fields?.fontSize || 48,
			headerFontSizeMobile: (headerTitleComponent?.fields?.fontSize || 40) * 0.8, // 80% of header size
			titleFontSizeMobile: (firstTitleComponent?.fields?.fontSize || 48) * 0.33, // 33% of title size
			buttonFontSize: firstButtonComponent?.fields?.fontSize || 14,
			buttonFontSizeMobile: (firstButtonComponent?.fields?.fontSize || 14) * 0.91, // 91% of button size
		};
	}, [fields?.components]);
	
	const extractActionsFromContentful = () => {
		if (!fields?.components) return [];
		
		const mediaCardSection = fields.components.find(
			comp => comp.sys?.contentType?.sys?.id === "mediaCardSection"
		);
		
		if (!mediaCardSection?.fields?.mediaCards) return [];
		
		return mediaCardSection.fields.mediaCards.map(card => {
			let title = "";
			let buttonText = "";
			let buttonUrl = "";
			let backgroundColor = "";
			let titleFontSize = 48;
			let buttonFontSize = 14;
			
			if (card.fields?.contents) {
				card.fields.contents.forEach(content => {
					if (content.sys?.contentType?.sys?.id === "title") {
						title = content.fields?.text?.content?.[0]?.content?.[0]?.value || "";
						titleFontSize = content.fields?.fontSize || 48;
					} else if (content.sys?.contentType?.sys?.id === "buttonGroup") {
						buttonText = content.fields?.button1Text || "";
						buttonUrl = content.fields?.button1Url || "";
						buttonFontSize = content.fields?.fontSize || 14;
					}
				});
			}
			
			// Use resolveColor for dynamic color resolution like other components
			const colorMapping = {
				"blue": resolveColor("blue") || "#4A90E2",
				"purple": resolveColor("purple") || "#8E44AD", 
				"yellow": resolveColor("yellow") || "#F39C12",
				"red": resolveColor("red") || "#E74C3C"
			};
			
			backgroundColor = colorMapping[card.fields?.contentBackgroundColor?.fields?.color] || resolveColor(card.fields?.contentBackgroundColor) || "#4A90E2";
			
			return {
				title,
				buttonText,
				buttonUrl,
				backgroundColor,
				titleFontSize,
				buttonFontSize,
				description: ""
			};
		});
	};
	
	const actions = extractActionsFromContentful().length > 0 
		? extractActionsFromContentful() 
		: [
			{
				title: "Scheduling An Advising Session",
				buttonText: "BOOK NOW",
				buttonUrl: "/schedule-advising-session",
				backgroundColor: resolveColor("blue") || "#4A90E2",
				titleFontSize: 48,
				buttonFontSize: 14,
				description: "Get personalized guidance for your career growth"
			},
			{
				title: "Take Your Assessment",
				buttonText: "TAKE AN ASSESSMENT",
				buttonUrl: "/assessment",
				backgroundColor: resolveColor("purple") || "#8E44AD",
				titleFontSize: 48,
				buttonFontSize: 14,
				description: "Evaluate your leadership skills and competencies"
			},
			{
				title: "Member Perks",
				buttonText: "BECOME A MEMBER",
				buttonUrl: "/membership",
				backgroundColor: resolveColor("yellow") || "#F39C12",
				titleFontSize: 48,
				buttonFontSize: 14,
				description: "Access exclusive benefits and resources"
			},
			{
				title: "Access Knowledge",
				buttonText: "DISCOVER RESOURCES",
				buttonUrl: "/resources",
				backgroundColor: resolveColor("red") || "#E74C3C",
				titleFontSize: 48,
				buttonFontSize: 14,
				description: "Explore our comprehensive knowledge library"
			}
		];
	
	const getHeaderContent = () => {
		if (!fields?.components) return null;
		
		const titleComponent = fields.components.find(
			comp => comp.sys?.contentType?.sys?.id === "title"
		);
		
		if (titleComponent) {
			return renderItem(titleComponent);
		}
		
		return null;
	};
	
	const backgroundColor = resolveColor(fields?.backgroundColor) || "#F8F9FA";

	return (
		<section 
			className="outerWrapper ActionItems" 
			id={fields?.hashLink}
		>
			<div className="innerWrapper">
				<Container fluid className="px-4 px-md-5">
					{getHeaderContent() && (
						<div className="header-section text-center mb-5">
							{getHeaderContent()}
						</div>
					)}
					
					{!getHeaderContent() && (
						<div className="header-section text-center mb-5">
							<h2 className="action-header">
								Join 250,000+ Healthcare Leaders Who Took Control Of Their Career Growth
							</h2>
						</div>
					)}

					<Row
						className="g-0 gy-4 justify-content-center d-flex"
					>
						{actions.map((action, index) => (
							<Col 
								key={index}
								className="justify-content-center align-items-stretch d-flex px-2"
								xs={12}
								sm={6}
								md={6}
								lg={3}
							>
								<div className="action-card" style={{ backgroundColor: action.backgroundColor, width: "100%" }}>
									<div className="action-content">
										<h1 
											className="action-title" 
											style={{ fontSize: `${action.titleFontSize}px` }}
										>
											{action.title}
										</h1>
								
										<div className="action-button-wrapper">
											<ButtonGroup
												btn1={{
													text: action.buttonText,
													url: action.buttonUrl,
												}}
												button1Variant="dark"
												buttonWidth={{ xs: "auto", sm: "auto", md: "auto", lg: "auto" }}
												onClick={() => {}}
												fontSize={action.buttonFontSize}
											/>
										</div>
									</div>
								</div>
							</Col>
						))}
					</Row>

					{fields?.bottomComponents && (
						<Row className="g-0 justify-content-center mt-5">
							{fields.bottomComponents.map((component) => {
								const align = component?.fields?.textAlign
									? `justify-content-${component?.fields?.textAlign}`
									: "justify-content-center";
								return (
									<Col className={`${align} d-flex w-100`} key={component.sys.id}>
										{component}
									</Col>
								);
							})}
						</Row>
					)}
				</Container>
			</div>

			<style jsx>{`
				.outerWrapper {
					background: ${backgroundColor};
					margin-top: ${spacingStyles.marginTop}em;
					padding-top: ${spacingStyles.paddingTop}em;
					padding-bottom: ${spacingStyles.paddingBottom}em;
					margin-bottom: ${spacingStyles.marginBottom}em;
					position: relative;
				}

				@media (max-width: 767.98px) {
					.outerWrapper {
						margin-top: ${spacingStyles.marginTopMobile}em;
						padding-top: ${spacingStyles.paddingTopMobile}em;
						padding-bottom: ${spacingStyles.paddingBottomMobile}em;
						margin-bottom: ${spacingStyles.marginBottomMobile}em;
					}
				}

				.innerWrapper {
					background: ${backgroundColor};
				}

				.header-section {
					margin-bottom: 4rem;
					position: relative;
				}

				.action-header {
					color: ${resolveColor(fields?.headerColor) || "#2C3E50"};
					font-family: ${font?.athelas || "Athelas, serif"};
					font-size: ${fontSizes.headerFontSize}px;
					font-weight: 600;
					line-height: 1.2;
					margin-bottom: 0;
					letter-spacing: -0.02em;
				}

				.action-card {
					border-radius: 8px;
					padding: 2.5rem 1.5rem;
					height: 100%;
					display: flex;
					flex-direction: column;
					justify-content: space-between;
					box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
					transition: transform 0.2s ease, box-shadow 0.2s ease;
					min-height: 200px;
				}

				.action-card:hover {
					transform: translateY(-2px);
					box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
				}

				.action-content {
					display: flex;
					flex-direction: column;
					height: 100%;
					justify-content: space-between;
					align-items: center;
				}

				.action-title {
					color: white;
					font-family: ${font?.athelas || "Athelas, serif"};
					font-weight: 600;
					line-height: 1.3;
					margin-bottom: 1rem;
					text-align: center;
					letter-spacing: 0.01em;
					padding-left: 1rem;
					padding-right: 1rem;
				}

				.action-description {
					color: rgba(255, 255, 255, 0.9);
					font-size: 0.85rem;
					line-height: 1.4;
					margin-bottom: 1.5rem;
					text-align: center;
					flex-grow: 1;
				}

				.action-button-wrapper {
					margin-top: auto;
					width: 100%;
					display: flex;
					justify-content: center;
					align-items: center;
				}

				/* EXACT button styling from image - override ButtonGroup styles */
				:global(.action-card .ButtonGroupComponent) {
					margin: 0;
					width: auto !important;
					justify-content: center !important;
				}

				:global(.action-card .ButtonGroupComponent .col-12) {
					width: auto !important;
					flex: 0 0 auto !important;
				}

				:global(.action-card .btn-styles) {
					background: #2C3E50 !important;
					color: white !important;
					border: none !important;
					border-radius: 6px !important;
					padding: 0.875rem 1.25rem !important;
					font-weight: 600 !important;
					text-decoration: none !important;
					text-align: center !important;
					transition: all 0.2s ease !important;
					display: inline-block !important;
					width: auto !important;
					min-width: fit-content !important;
					min-height: auto !important;
					font-family: ${font?.openSans || "Open Sans, sans-serif"} !important;
					letter-spacing: 0.02em !important;
					text-transform: none !important;
				}

				:global(.action-card .btn-styles:hover) {
					background: #34495E !important;
					color: white !important;
					text-decoration: none !important;
					box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
					transform: translateY(-1px) !important;
				}

				@media (max-width: 767.98px) {
					.header-section {
						margin-bottom: 3rem;
					}

					.action-header {
						font-size: ${fontSizes.headerFontSizeMobile}px;
					}

					.action-card {
						padding: 2rem 1.25rem;
						min-height: 180px;
					}

					.action-title {
						/* Font size is now controlled inline per card */
					}

					:global(.action-card .btn-styles) {
						padding: 0.75rem 1rem !important;
					}
				}

				@media (min-width: 768px) and (max-width: 1199px) {
					.action-card {
						min-height: 220px;
					}
				}
			`}</style>
		</section>
	);
}
